import { WordFragment } from "../create";
import { OptionsParagraph } from "./paragraph";
import { Relationship } from "./relationship";
import { OptionsRpr } from "./rpr";

/**
 * Create element
 */
abstract class CreateElement {
    /**
     * External defaults
     */
    protected externalDefaults: Map<string, string>;

    /**
     * External relationships
     */
    protected externalRelationships: Relationship[];

    /**
     * File contents
     */
    protected fileContents: Map<string, ArrayBuffer>;

    /**
     * XML content
     */
    protected xml: string;

    /**
     * Constructor
     */
    constructor() {
        this.externalDefaults = new Map<string, string>();
        this.externalRelationships = [];
        this.fileContents = new Map<string, ArrayBuffer>();
        this.xml = "";
    }

    /**
     * Create element content
     */
    abstract create(): string;

    /**
     * Getter external defaults
     *
     * @returns external relationships
     */
    public getExternalDefaults(): Map<string, string> {
        return this.externalDefaults;
    }

    /**
     * Getter external relationships
     *
     * @returns external relationships
     */
    public getExternalRelationships(): Relationship[] {
        return this.externalRelationships;
    }

    /**
     * Getter file contents
     *
     * @returns File contents
     */
    public getFileContents(): Map<string, ArrayBuffer> {
        return this.fileContents;
    }

    /**
     * Getter xml
     *
     * @returns XML
     */
    public getXml(): string {
        return this.xml;
    }

    /**
     * Merge rPr styles in paragraph options
     *
     * @param options - Style options
     * @param optionsParagraph - Paragraph options
     * @returns options
     */
    public mergeParagraphRprStyles<T extends OptionsRpr>(
        options: T,
        optionsParagraph: OptionsParagraph
    ): T {
        // merge rPr styles.
        // If the paragraph contains a rPr style it must be applied as text style if the element doesn't alreay contains it
        if (optionsParagraph != null) {
            options.bold ??= optionsParagraph.bold ?? undefined;
            options.color ??= optionsParagraph.color ?? undefined;
            options.font ??= optionsParagraph.font ?? undefined;
            options.fontSize ??= optionsParagraph.fontSize ?? undefined;
            options.highlight ??= optionsParagraph.highlight ?? undefined;
            options.italic ??= optionsParagraph.italic ?? undefined;
            options.lang ??= optionsParagraph.lang ?? undefined;
            options.strikethrough ??=
                optionsParagraph.strikethrough ?? undefined;
            options.underline ??= optionsParagraph.underline ?? undefined;
        }

        return options;
    }

    /**
     * Keeps external dependencies from the WordFragment
     *
     * @param element - WordFragment
     */
    protected mergeExternalDependencies(element: WordFragment) {
        // keep external relationships from the element
        this.externalRelationships = [
            ...this.externalRelationships,
            ...element.getExternalRelationships(),
        ];
        // keep defaults from the element
        this.externalDefaults = new Map([
            ...this.externalDefaults,
            ...element.getExternalDefaults(),
        ]);
        // keep files from the element
        this.fileContents = new Map([
            ...this.fileContents,
            ...element.getDocxContent().getFileContents(),
        ]);
    }
}

export { CreateElement };
